import { useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Image,
  Card,
  Spinner,
  Chip,
  Tooltip,
} from "@heroui/react";
import { useNavigate } from "@remix-run/react";
import { FiArrowRight } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { removeFromCartApi } from "app/api/cart/cart.api";
import { cartList } from "app/atoms/cart.atom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { userAtom, needsToLoginAtom } from "app/atoms/user.atom";
import { addSpacesToInteger, formatToTwoDecimalPlaces } from "app/utils/fn";

export default function CartComponent({
  onCartOpenChange,
}: {
  onCartOpenChange: ((isOpen: boolean) => void) | undefined;
}) {
  const [cartsList, setCartList] = useAtom(cartList);
  const [isDeleteCartLoading, setIsDeleteCartLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const setNeedsToLogin = useSetAtom(needsToLoginAtom);

  // Calculate totals
  const total = cartsList.reduce(
    (sum, item) => sum + (Number(item?.calculatedPrice?.totalPrice) || 0),
    0,
  );
  const gstPercentage = 10; // Assuming 10% GST
  const gstAmount = (total * gstPercentage) / 100;
  const finalTotal = total + gstAmount;

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setIsDeleteCartLoading(true);
    removeFromCartApi(id)
      .then(() => {
        const filteredItems = cartsList.filter((item) => item._id !== id);
        setCartList(filteredItems);
      })
      .catch((error: any) => {
        const isNotFound = error?.response?.status === 404;
        if (isNotFound) {
          // If item not found on server, silently remove from local state
          const filteredItems = cartsList.filter((item) => item._id !== id);
          setCartList(filteredItems);
        } else {
          console.error("Error removing item from cart:", error);
        }
      })
      .finally(() => {
        setIsDeleteCartLoading(false);
      });
  };

  const handleCheckoutRoute = () => {
    if (user && user._id) {
      if (onCartOpenChange) {
        onCartOpenChange(false);
      }
      navigate("/checkout");
    } else {
      setNeedsToLogin(true);
    }
  };

  // Render attributes as a list of chips
  const renderAttributes = (attributes?: { key: string; value: string }[]) => {
    if (!attributes || attributes.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {attributes.map((attr, index) => (
          <Chip key={index} size="sm" variant="flat" color="secondary">
            {attr.key}: {attr.value.trim()}
          </Chip>
        ))}
      </div>
    );
  };

  if (cartsList.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Your Cart is Empty</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Looks like you haven't added any items to your cart yet.
          </p>
          <Button
            onPress={() => {
              if (onCartOpenChange) {
                onCartOpenChange(false);
              }
              navigate("/");
            }}
            color="primary"
            size="lg"
            className="font-semibold"
          >
            Start Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col xl:flex-row gap-4 sm:gap-6 lg:gap-6">
        {/* Desktop Table View */}
        <div className="flex-grow hidden md:block">
          <Table
            aria-label="Cart items"
            removeWrapper
            classNames={{
              th: "bg-primary/10 text-foreground font-semibold text-xs sm:text-sm",
              td: "py-3 sm:py-4 text-xs sm:text-sm",
              wrapper: "overflow-x-auto",
            }}
          >
            <TableHeader>
              <TableColumn className="w-[1.5rem]"> </TableColumn>
              <TableColumn>PRODUCT</TableColumn>
              <TableColumn>COLOUR</TableColumn>
              <TableColumn>PRICE</TableColumn>
              <TableColumn>QUANTITY</TableColumn>
              <TableColumn>SUBTOTAL</TableColumn>
            </TableHeader>
            <TableBody>
              {cartsList.map((item) => (
                <TableRow key={item._id} className="hover:bg-default-50/30 transition-colors">
                  <TableCell className="w-[1.5rem] px-1">
                    {isDeleteCartLoading && item._id === selectedId ? (
                      <Spinner size="sm" />
                    ) : (
                      <button
                        onClick={() => handleDelete(item._id as string)}
                        className="text-danger hover:text-danger/70 transition-colors"
                        aria-label={`Remove ${item?.productId?.overview?.name}`}
                      >
                        <RxCross2 size={20} />
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 sm:gap-4">
                      <Image
                        src={item?.productId?.overview?.heroImage}
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg border border-default-100 flex-shrink-0"
                        alt={item?.productId?.overview?.name}
                      />
                      <div className="min-w-0 flex-1">
                        <Tooltip
                          content={
                            <div className="max-w-xs">
                              <div className="font-medium">{item?.productId?.overview?.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-300 mt-1">
                                Code: {item?.productId?.overview?.code}
                              </div>
                              {item.brandingInfo && item.brandingInfo?.type && (
                                <div className="text-sm text-gray-500 dark:text-gray-300 mt-1">
                                  <span className="font-medium"> Artwork:</span>{" "}
                                  {item.brandingInfo?.type}
                                  <br />
                                  {item?.brandingInfo?.position ? (
                                    <span className=" font-medium">
                                      Branding Position/s: {item?.brandingInfo?.position}
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              )}
                            </div>
                          }
                          placement="top"
                          delay={300}
                        >
                          <div className="font-medium text-xs sm:text-sm truncate cursor-help">
                            {item?.productId?.overview?.name}
                          </div>
                        </Tooltip>
                        <div className="italic text-xs text-gray-500 truncate">
                          - ({item?.productId?.overview?.code})
                        </div>
                        {item.brandingInfo && item.brandingInfo?.type && (
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-5">
                            <span className="font-bold"> Artwork:</span> {item.brandingInfo?.type}{" "}
                            <br />
                            {item?.brandingInfo?.position ? (
                              <span>
                                <span className=" font-bold"> Branding Position/s:</span>{" "}
                                {item?.brandingInfo?.position}
                              </span>
                            ) : (
                              ""
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{renderAttributes(item.attributes)}</TableCell>
                  <TableCell className="font-medium">
                    ${formatToTwoDecimalPlaces(item.calculatedPrice?.unitPrice || 0)}
                  </TableCell>
                  <TableCell>
                    <Chip variant="flat" color="primary">
                      {addSpacesToInteger(item.quantity)}
                    </Chip>
                  </TableCell>
                  <TableCell className="font-medium text-primary text-right">
                    ${formatToTwoDecimalPlaces(Number(item?.calculatedPrice?.totalPrice) || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile/Tablet View */}
        <div className="block md:hidden space-y-3 sm:space-y-4">
          {cartsList.map((item) => (
            <div
              key={item._id}
              className="bg-default-100 rounded-lg sm:rounded-xl p-3 sm:p-4 relative shadow-sm"
            >
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                {isDeleteCartLoading && item._id === selectedId ? (
                  <Spinner size="sm" />
                ) : (
                  <button
                    onClick={() => handleDelete(item._id as string)}
                    className="text-danger hover:text-danger/70 transition-colors p-1"
                    aria-label={`Remove ${item?.productId?.overview?.name}`}
                  >
                    <RxCross2 size={18} className="sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>

              {/* Product Info */}
              <div className="flex gap-3 sm:gap-4 pr-8">
                <Image
                  src={item?.productId?.overview?.heroImage}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg border border-default-100 flex-shrink-0"
                  alt={item?.productId?.overview?.name}
                />
                <div className="flex-1 min-w-0">
                  <Tooltip
                    content={
                      <div className="max-w-xs">
                        <div className="font-medium">{item?.productId?.overview?.name}</div>
                        <div className="text-sm text-gray-300 mt-1">
                          Code: {item?.productId?.overview?.code}
                        </div>
                        {item.brandingInfo && item.brandingInfo?.type && (
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-5">
                            <span className="font-bold"> Artwork:</span> {item.brandingInfo?.type}{" "}
                            <br />
                            {item?.brandingInfo?.position ? (
                              <span>
                                <span className=" font-bold"> Branding Position/s:</span>{" "}
                                {item?.brandingInfo?.position}
                              </span>
                            ) : (
                              ""
                            )}
                          </p>
                        )}
                      </div>
                    }
                    placement="top"
                    delay={300}
                  >
                    <h3 className="font-medium text-sm sm:text-base lg:text-lg mb-1 line-clamp-2 cursor-help">
                      {item?.productId?.overview?.name}{" "}
                      <span className="italic text-gray-500">
                        - ({item?.productId?.overview?.code})
                      </span>
                    </h3>
                  </Tooltip>
                  {item.brandingInfo && item.brandingInfo?.type && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-5">
                      <span className="font-bold"> Artwork:</span> {item.brandingInfo?.type} <br />
                      {item?.brandingInfo?.position ? (
                        <span>
                          <span className=" font-bold"> Branding Position/s:</span>{" "}
                          {item?.brandingInfo?.position}
                        </span>
                      ) : (
                        ""
                      )}
                    </p>
                  )}
                  <div className="mb-2">{renderAttributes(item.attributes)}</div>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs sm:text-sm">
                    <span className="text-gray-500">Price:</span>
                    <span className="font-medium text-right">
                      ${formatToTwoDecimalPlaces(item.calculatedPrice?.unitPrice || 0)}
                    </span>
                    <span className="text-gray-500">Quantity:</span>
                    <div className="text-right">
                      <Chip variant="flat" color="primary" size="sm" className="text-xs">
                        {addSpacesToInteger(item.quantity)}
                      </Chip>
                    </div>
                    <span className="text-gray-500">Subtotal:</span>
                    <span className="font-medium text-primary text-right">
                      ${formatToTwoDecimalPlaces(Number(item?.calculatedPrice?.totalPrice) || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="w-full xl:w-[26rem] 2xl:w-[28rem]">
          <div className="bg-default-100 rounded-lg sm:rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6 shadow-md sticky top-4">
            <h2 className="text-lg sm:text-xl font-bold text-foreground">Order Summary</h2>
            <div className="space-y-3 sm:space-y-4">
              <div className="border-t border-gray-200 pt-3 sm:pt-4 space-y-2">
                <div className="flex justify-between font-bold text-sm sm:text-base">
                  <span>Total (ex GST)</span>
                  <span className="text-primary">${formatToTwoDecimalPlaces(total)}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span>GST ({gstPercentage}%)</span>
                  <span className="text-primary">${formatToTwoDecimalPlaces(gstAmount)}</span>
                </div>
                <div className="flex justify-between font-bold text-base sm:text-lg">
                  <span>Total (inc GST)</span>
                  <span className="text-primary">${formatToTwoDecimalPlaces(finalTotal)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <Button
                onPress={handleCheckoutRoute}
                fullWidth
                color="primary"
                size="lg"
                className="font-semibold h-12 sm:h-14 text-sm sm:text-base"
                endContent={<FiArrowRight className="text-lg sm:text-xl" />}
              >
                Proceed to Checkout
              </Button>
              <Button
                onPress={() => {
                  if (onCartOpenChange) {
                    onCartOpenChange(false);
                  }
                  navigate(-1);
                }}
                fullWidth
                variant="bordered"
                className="font-medium h-10 sm:h-12 text-sm sm:text-base"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
